import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Alert, Badge, Button, Form, Spinner, Modal } from "react-bootstrap";
import * as Constants from "../api/constants";
import "../partyBossTheme.css";

const CARE_NUMBER_DEFAULT = "+91 7780291217";

/**
 * Normalise everything the user types — strip non-digits and keep at most the last 10 digits so
 * users with `+91`, dashes or spaces all hit the same lookup key.
 */
function digitsOnly(value) {
  const d = String(value || "").replace(/\D/g, "");
  return d.length > 10 ? d.slice(-10) : d;
}

function isValidPhone(value) {
  return /^\d{10}$/.test(digitsOnly(value));
}

function formatEventDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function buildTel(care) {
  return `tel:${String(care || "").replace(/\s+/g, "")}`;
}

/**
 * The category labels DB rows use (see `com.pb.webapp.constants.Addons`). Keep this list aligned
 * with the backend constants — strings here drive both grouping and the per-category icons.
 */
const CATEGORY_META = [
  { key: "DECORATION", label: "Decoration", icon: "bi-balloon" },
  { key: "CAKES", label: "Cakes", icon: "bi-cake2" },
  { key: "PARTY PROPS", label: "Party props", icon: "bi-stars" },
  { key: "PHOTOS", label: "Photos", icon: "bi-camera-fill" },
  { key: "FLOWERS", label: "Flowers", icon: "bi-flower1" },
  { key: "GIFTS", label: "Gifts", icon: "bi-gift-fill" },
];

/**
 * Public "My Booking" page. Two phases:
 *
 *   1. Phone entry — customer types their 10-digit number and we call /myBooking/lookup.
 *   2. Active booking view — shows a read-only summary of existing addons (with a
 *      "call us to change" hint), an editor for the cake / decoration name, and a picker for
 *      adding more addons. Existing items cannot be removed; new ones append-only.
 *
 * Past / cancelled / not-found phone numbers get a friendly message + a Book now CTA + the
 * customer-care phone link, instead of a destructive "no booking" empty state.
 */
export default function MyBooking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookup, setLookup] = useState(null); // backend MyBookingLookupResponse
  const [careNumber, setCareNumber] = useState(CARE_NUMBER_DEFAULT);

  // Active-state UI
  const [catalog, setCatalog] = useState({});
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCategory, setPickerCategory] = useState(null);
  const [newAddons, setNewAddons] = useState([]); // chosen-but-not-yet-saved addons
  const [nameOnCake, setNameOnCake] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [updateBanner, setUpdateBanner] = useState(null);

  useEffect(() => {
    const raw = searchParams.get("phone");
    if (!raw) return;
    const d = digitsOnly(raw);
    if (!/^\d{10}$/.test(d)) return;
    setPhone((prev) => {
      const pd = digitsOnly(prev);
      if (pd.length >= 10) return prev;
      return d;
    });
  }, [searchParams]);

  /** Booking handle once the lookup returns ACTIVE. */
  const booking = lookup?.status === "ACTIVE" ? lookup.booking : null;

  // -------------------- lookup --------------------
  const onSubmitPhone = useCallback(
    async (e) => {
      e?.preventDefault?.();
      setPhoneError("");
      const digits = digitsOnly(phone);
      if (!isValidPhone(digits)) {
        setPhoneError("Please enter a valid 10-digit phone number.");
        return;
      }
      setLookupLoading(true);
      setLookup(null);
      try {
        const { data } = await axios.get(Constants.MY_BOOKING_LOOKUP, {
          params: { phoneNumber: digits },
        });
        setLookup(data);
        if (data?.customerCareNumber) setCareNumber(data.customerCareNumber);
        if (data?.status === "ACTIVE") {
          setNameOnCake(data.booking?.nameOnCake || "");
          setNewAddons([]);
          setUpdateBanner(null);
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          "We couldn't reach our booking system. Please try again or call us at " +
            careNumber +
            ".";
        setLookup({ status: "ERROR", message: msg });
      } finally {
        setLookupLoading(false);
      }
    },
    [phone, careNumber]
  );

  // -------------------- catalog (only when active) --------------------
  useEffect(() => {
    if (lookup?.status !== "ACTIVE") return undefined;
    let cancelled = false;
    setCatalogLoading(true);
    axios
      .get(Constants.GET_META_DATA_ADDONS)
      .then((res) => {
        if (cancelled) return;
        setCatalog(res?.data?.addonsData || {});
      })
      .catch(() => {
        if (!cancelled) setCatalog({});
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lookup?.status]);

  // -------------------- derived --------------------
  const existingIds = useMemo(
    () => new Set((booking?.existingAddonIds || []).map((id) => Number(id))),
    [booking?.existingAddonIds]
  );

  const newAddonsTotal = useMemo(
    () => newAddons.reduce((sum, a) => sum + Number(a.price || 0), 0),
    [newAddons]
  );

  const projectedFinal = useMemo(() => {
    const cur = Number(booking?.finalPrice || 0);
    return cur + newAddonsTotal;
  }, [booking?.finalPrice, newAddonsTotal]);

  const projectedDue = useMemo(() => {
    const cur = Number(booking?.amountToBePaid || booking?.finalPrice || 0);
    return Math.max(0, cur + newAddonsTotal);
  }, [booking?.amountToBePaid, booking?.finalPrice, newAddonsTotal]);

  const nameChanged = useMemo(() => {
    const a = String(nameOnCake || "").trim();
    const b = String(booking?.nameOnCake || "").trim();
    return a !== b;
  }, [nameOnCake, booking?.nameOnCake]);

  // -------------------- picker --------------------
  const openPicker = (cat) => {
    setPickerCategory(cat);
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerCategory(null);
  };

  const toggleNewAddon = (addon) => {
    if (existingIds.has(Number(addon.id))) return; // safety: never let them pick what's already locked
    setNewAddons((prev) => {
      const has = prev.some((a) => Number(a.id) === Number(addon.id));
      if (has) return prev.filter((a) => Number(a.id) !== Number(addon.id));
      return [...prev, addon];
    });
  };

  const removeNewAddon = (addon) => {
    setNewAddons((prev) => prev.filter((a) => Number(a.id) !== Number(addon.id)));
  };

  // -------------------- submit --------------------
  const onSubmitUpdate = useCallback(async () => {
    if (!booking) return;
    if (newAddons.length === 0 && !nameChanged) {
      setUpdateBanner({
        variant: "warning",
        message:
          "Pick at least one new addon, or change the name on the cake before saving.",
      });
      return;
    }
    setSubmitting(true);
    setUpdateBanner(null);
    try {
      const { data } = await axios.post(Constants.MY_BOOKING_ADD_ADDONS, {
        bookingReferenceId: booking.bookingReferenceId,
        phoneNumber: digitsOnly(phone),
        nameOnCake: nameChanged ? nameOnCake.trim() : null,
        newAddons,
      });
      if (data?.success) {
        setLookup((prev) => ({ ...prev, booking: data.booking }));
        setNewAddons([]);
        setNameOnCake(data.booking?.nameOnCake || "");
        setUpdateBanner({
          variant: "success",
          message:
            data.message ||
            "Booking updated. We've emailed you a fresh confirmation with the new totals.",
        });
      } else {
        setUpdateBanner({
          variant: "danger",
          message: data?.message || "We couldn't update your booking. Please try again.",
        });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "We couldn't update your booking right now. Please try again or call us at " +
          careNumber +
          ".";
      setUpdateBanner({ variant: "danger", message: msg });
    } finally {
      setSubmitting(false);
    }
  }, [booking, newAddons, nameOnCake, nameChanged, phone, careNumber]);

  const startOver = () => {
    setLookup(null);
    setNewAddons([]);
    setNameOnCake("");
    setUpdateBanner(null);
  };

  // -------------------- render --------------------
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0d2e] via-[#0f071c] to-[#0a0614] text-white overflow-x-clip">
      <div className="mx-auto w-full max-w-5xl px-3 py-8 sm:px-6 sm:py-12">
        <header className="mb-6 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#ffd166]">
            <i className="bi bi-bag-check-fill" />
            My Booking
          </span>
          <h1 className="mt-3 text-2xl font-black sm:text-4xl">
            Manage your Party Boss booking
          </h1>
          <p className="mt-2 text-sm text-white/70 sm:text-base">
            Enter the phone number you used while booking. You'll see your party details and
            can add more goodies right away.
          </p>
        </header>

        {/* Phase 1 — phone entry */}
        {!booking ? (
          <section className="mx-auto max-w-xl rounded-2xl border border-white/10 bg-[#12081f] p-5 shadow-xl ring-1 ring-white/5 sm:p-6">
            <Form onSubmit={onSubmitPhone}>
              <Form.Group className="mb-3">
                <Form.Label className="text-white/80 text-sm font-semibold">
                  Phone number used at booking
                </Form.Label>
                <Form.Control
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={15}
                  placeholder="98XXXXXXXX"
                  className="party-boss-form-control rounded-xl border border-white/20 bg-[#12081f] py-2.5 text-base text-[#f1f5f9] placeholder:text-slate-400"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  isInvalid={Boolean(phoneError)}
                />
                <Form.Control.Feedback type="invalid">
                  {phoneError}
                </Form.Control.Feedback>
              </Form.Group>
              <Button
                type="submit"
                disabled={lookupLoading}
                className="w-full rounded-xl bg-gradient-to-r from-[#ffd166] to-[#ff8fab] py-2.5 text-sm font-black uppercase tracking-wide text-[#120817] border-0"
              >
                {lookupLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Looking up…
                  </>
                ) : (
                  <>Find my booking</>
                )}
              </Button>
            </Form>

            {lookup && lookup.status !== "ACTIVE" ? (
              <NonActiveState
                lookup={lookup}
                careNumber={careNumber}
                onTryAgain={startOver}
                onBookNow={() => navigate("/booknow")}
              />
            ) : null}

            <div className="mt-4 border-t border-white/10 pt-3 text-center text-xs text-white/60">
              Need help? Call <a href={buildTel(careNumber)} className="text-[#ffd166]">{careNumber}</a>
              {" · "}
              <Link to="/booknow" className="text-[#ffd166]">
                Book a new party
              </Link>
            </div>
          </section>
        ) : (
          /* Phase 2 — active booking */
          <ActiveBookingView
            booking={booking}
            careNumber={careNumber}
            catalog={catalog}
            catalogLoading={catalogLoading}
            existingIds={existingIds}
            newAddons={newAddons}
            newAddonsTotal={newAddonsTotal}
            projectedFinal={projectedFinal}
            projectedDue={projectedDue}
            nameOnCake={nameOnCake}
            setNameOnCake={setNameOnCake}
            nameChanged={nameChanged}
            updateBanner={updateBanner}
            submitting={submitting}
            onOpenPicker={openPicker}
            onRemoveNewAddon={removeNewAddon}
            onSubmitUpdate={onSubmitUpdate}
            onStartOver={startOver}
          />
        )}
      </div>

      {/* Addon picker modal */}
      <Modal
        show={pickerOpen}
        onHide={closePicker}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Add more {pickerCategory ? CATEGORY_META.find((c) => c.key === pickerCategory)?.label || pickerCategory : "addons"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Tap to add. Items already on your booking are dimmed — call us at {" "}
            <a href={buildTel(careNumber)}>{careNumber}</a> if you need to change them.
          </p>
          <div className="row g-3">
            {(catalog?.[pickerCategory] || []).length === 0 ? (
              <div className="text-muted small">No items available in this category right now.</div>
            ) : null}
            {(catalog?.[pickerCategory] || []).map((addon) => {
              const locked = existingIds.has(Number(addon.id));
              const selected = newAddons.some((a) => Number(a.id) === Number(addon.id));
              return (
                <div className="col-12 col-sm-6" key={addon.id}>
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => toggleNewAddon(addon)}
                    className={`w-100 text-start rounded border p-3 d-flex justify-content-between align-items-center ${
                      locked
                        ? "bg-light text-muted"
                        : selected
                          ? "border-success bg-success bg-opacity-10"
                          : "bg-white"
                    }`}
                    style={{ minHeight: 76, opacity: locked ? 0.6 : 1 }}
                  >
                    <div>
                      <div className="fw-semibold">{addon.itemName}</div>
                      <div className="small text-muted">
                        {locked
                          ? "Already on your booking — call to change"
                          : selected
                            ? "Selected — tap to remove"
                            : "Tap to add"}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="fw-bold text-dark">
                        <i className="bi bi-currency-rupee" />
                        {addon.price}
                      </div>
                      {selected ? (
                        <i className="bi bi-check-circle-fill text-success" aria-hidden />
                      ) : null}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closePicker}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

function NonActiveState({ lookup, careNumber, onTryAgain, onBookNow }) {
  const tone =
    lookup?.status === "PAST_COMPLETED"
      ? "info"
      : lookup?.status === "CANCELLED_ONLY"
        ? "warning"
        : lookup?.status === "ERROR"
          ? "danger"
          : "secondary";
  const icon =
    lookup?.status === "PAST_COMPLETED"
      ? "bi-stars"
      : lookup?.status === "CANCELLED_ONLY"
        ? "bi-calendar-x"
        : lookup?.status === "ERROR"
          ? "bi-exclamation-triangle"
          : "bi-search";
  const title =
    lookup?.status === "PAST_COMPLETED"
      ? "Your party already happened"
      : lookup?.status === "CANCELLED_ONLY"
        ? "Past bookings only"
        : lookup?.status === "ERROR"
          ? "Couldn't reach the server"
          : "No booking found";
  return (
    <div className="mt-4">
      <Alert variant={tone} className="d-flex align-items-start gap-3">
        <i className={`bi ${icon} fs-3`} aria-hidden />
        <div>
          <div className="fw-bold">{title}</div>
          <div>{lookup?.message}</div>
          {lookup?.status === "PAST_COMPLETED" && lookup?.lastPartyDate ? (
            <div className="small text-muted mt-1">
              Most recent party: {formatEventDate(lookup.lastPartyDate)}
            </div>
          ) : null}
        </div>
      </Alert>
      <div className="d-flex flex-wrap gap-2">
        <Button variant="outline-light" onClick={onTryAgain}>
          Try a different number
        </Button>
        <Button variant="warning" onClick={onBookNow}>
          <i className="bi bi-calendar2-event me-1" />
          Book a new party
        </Button>
        <a
          href={buildTel(careNumber)}
          className="btn btn-outline-success d-inline-flex align-items-center"
        >
          <i className="bi bi-telephone-fill me-1" />
          Call {careNumber}
        </a>
      </div>
    </div>
  );
}

function ActiveBookingView({
  booking,
  careNumber,
  catalog,
  catalogLoading,
  existingIds,
  newAddons,
  newAddonsTotal,
  projectedFinal,
  projectedDue,
  nameOnCake,
  setNameOnCake,
  nameChanged,
  updateBanner,
  submitting,
  onOpenPicker,
  onRemoveNewAddon,
  onSubmitUpdate,
  onStartOver,
}) {
  return (
    <section className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
      {/* Left column: booking details + addon catalog */}
      <div className="space-y-4">
        <SummaryCard booking={booking} careNumber={careNumber} onStartOver={onStartOver} />

        <ExistingAddonsCard booking={booking} careNumber={careNumber} />

        <div className="rounded-2xl border border-white/10 bg-[#12081f] p-4 shadow-lg sm:p-5">
          <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
            <div>
              <h3 className="text-base sm:text-lg font-black text-white mb-1">
                Add more goodies
              </h3>
              <p className="text-xs text-white/60 m-0">
                Pick anything new to add to your party. Items already locked in are dimmed.
              </p>
            </div>
            <Badge bg="warning" text="dark">
              {newAddons.length} new selected
            </Badge>
          </div>

          {catalogLoading ? (
            <div className="d-flex align-items-center gap-2 text-white/70">
              <Spinner animation="border" size="sm" />
              Loading addon catalog…
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
              {CATEGORY_META.map((cat) => {
                const items = catalog?.[cat.key] || [];
                const enabled = items.length > 0;
                return (
                  <button
                    key={cat.key}
                    type="button"
                    disabled={!enabled}
                    onClick={() => onOpenPicker(cat.key)}
                    className={`group flex w-full items-center justify-between rounded-xl border border-white/15 bg-white/5 px-3 py-3 text-left transition hover:border-[#ffd166]/60 hover:bg-white/10 disabled:opacity-40`}
                  >
                    <div className="flex items-center gap-2">
                      <i className={`bi ${cat.icon} text-[#ffd166]`} aria-hidden />
                      <span className="text-sm font-bold text-white">{cat.label}</span>
                    </div>
                    <span className="text-xs text-white/60">
                      {items.length} item{items.length === 1 ? "" : "s"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right column: name editor + new selections + price summary + submit */}
      <aside className="space-y-4">
        <div className="rounded-2xl border border-white/10 bg-[#12081f] p-4 shadow-lg sm:p-5">
          <h3 className="text-base sm:text-lg font-black text-white mb-2">
            Name on cake / decoration
          </h3>
          <p className="text-xs text-white/60 mb-3">
            You can update just the name printed on the cake or decoration. For anything else
            already on the booking, please call us.
          </p>
          <Form.Control
            className="party-boss-form-control rounded-xl border border-white/20 bg-[#12081f] py-2 text-[#f1f5f9] placeholder:text-slate-400"
            value={nameOnCake}
            onChange={(e) => setNameOnCake(e.target.value)}
            maxLength={120}
            placeholder="e.g. Happy Birthday Aarav"
          />
          {nameChanged ? (
            <div className="mt-2 text-xs text-emerald-300">
              <i className="bi bi-info-circle me-1" />
              Will be saved when you tap Save below.
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#12081f] p-4 shadow-lg sm:p-5">
          <h3 className="text-base sm:text-lg font-black text-white mb-2">
            New addons in this update
          </h3>
          {newAddons.length === 0 ? (
            <p className="text-sm text-white/60 m-0">
              Tap a category on the left to add cakes, decorations or party props.
            </p>
          ) : (
            <ul className="list-unstyled m-0 space-y-2">
              {newAddons.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{a.itemName}</div>
                    <div className="text-[11px] text-white/60">{a.categoryType}</div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-sm text-[#ffd166] font-bold">
                      <i className="bi bi-currency-rupee" />
                      {a.price}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() => onRemoveNewAddon(a)}
                      aria-label={`Remove ${a.itemName}`}
                    >
                      <i className="bi bi-x-circle-fill" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 border-t border-white/10 pt-3 text-sm text-white/80">
            <Row label="Current total" value={booking?.finalPrice} />
            <Row label="Already paid" value={booking?.amountPaid} muted />
            <Row label="New addons total" value={newAddonsTotal} highlight />
            <hr className="border-white/10 my-2" />
            <Row label="Projected total" value={projectedFinal} bold />
            <Row label="Balance due" value={projectedDue} bold accent />
          </div>

          {updateBanner ? (
            <Alert variant={updateBanner.variant} className="mt-3 mb-0">
              {updateBanner.message}
            </Alert>
          ) : null}

          <Button
            onClick={onSubmitUpdate}
            disabled={submitting || (newAddons.length === 0 && !nameChanged)}
            className="mt-3 w-full rounded-xl bg-gradient-to-r from-pink-500 to-violet-600 py-2.5 text-sm font-black uppercase tracking-wide text-white border-0"
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              <>
                <i className="bi bi-cart-plus me-1" />
                Save & email me a new confirmation
              </>
            )}
          </Button>

          <p className="mt-2 text-[11px] text-white/50 text-center">
            We'll email you and Party Boss instantly with the updated booking. Pay the new
            balance at the venue.
          </p>
        </div>
      </aside>
    </section>
  );
}

function SummaryCard({ booking, careNumber, onStartOver }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#1d0d3a] to-[#12081f] p-4 shadow-lg sm:p-5">
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#ffd166] font-bold">
            Booking ref
          </div>
          <div className="text-lg font-black text-white">
            {booking.bookingReferenceId}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end gap-1">
          <Badge bg="success">{booking.status || "Confirmed"}</Badge>
          <button
            type="button"
            onClick={onStartOver}
            className="text-xs text-white/60 hover:text-white border-0 bg-transparent p-0"
          >
            <i className="bi bi-arrow-left me-1" />
            Use a different number
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <Field label="Customer">{booking.customerName}</Field>
        <Field label="Phone">{booking.phoneNumber}</Field>
        {booking.email ? <Field label="Email">{booking.email}</Field> : null}
        <Field label="Event date">{formatEventDate(booking.eventDate)}</Field>
        {booking.slotTime ? <Field label="Slot">{booking.slotTime}</Field> : null}
        {booking.theatreName ? <Field label="Theatre">{booking.theatreName}</Field> : null}
        {booking.eventTypeName ? <Field label="Event type">{booking.eventTypeName}</Field> : null}
        {booking.totalSeats ? <Field label="Seats">{booking.totalSeats}</Field> : null}
      </div>
      <div className="mt-3 border-t border-white/10 pt-3 text-xs text-white/60">
        Need to change anything that's already locked in? Call Party Boss at {" "}
        <a href={buildTel(careNumber)} className="text-[#ffd166] fw-bold">
          {careNumber}
        </a>
        .
      </div>
    </div>
  );
}

function ExistingAddonsCard({ booking, careNumber }) {
  const grouped = booking?.existingAddons || {};
  const keys = Object.keys(grouped).filter((k) => grouped[k]?.length);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#12081f] p-4 shadow-lg sm:p-5">
      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
        <h3 className="text-base sm:text-lg font-black text-white m-0">
          Already on your booking
        </h3>
        <Badge bg="secondary">
          <i className="bi bi-lock-fill me-1" />
          Locked
        </Badge>
      </div>
      {keys.length === 0 ? (
        <p className="text-sm text-white/60 m-0">No addons on this booking yet.</p>
      ) : (
        <div className="space-y-3">
          {keys.map((cat) => (
            <div key={cat}>
              <div className="text-xs font-bold uppercase tracking-wider text-[#ffd166] mb-1">
                {cat}
              </div>
              <ul className="list-unstyled m-0 space-y-1">
                {grouped[cat].map((a) => (
                  <li
                    key={a.id}
                    className="d-flex justify-content-between align-items-center text-sm text-white/85 px-2 py-1 rounded bg-white/5"
                  >
                    <span className="truncate">
                      <i className="bi bi-check2-circle text-emerald-300 me-2" aria-hidden />
                      {a.itemName}
                    </span>
                    <span className="text-white/60 small">
                      <i className="bi bi-currency-rupee" />
                      {a.price}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="border-t border-white/10 pt-2 mt-2 text-[12px] text-white/55">
            <i className="bi bi-info-circle me-1" />
            To swap or remove anything above, please call us at {" "}
            <a href={buildTel(careNumber)} className="text-[#ffd166]">
              {careNumber}
            </a>
            .
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-white/50 font-bold">
        {label}
      </div>
      <div className="text-white font-semibold break-words">{children}</div>
    </div>
  );
}

function Row({ label, value, muted, bold, accent, highlight }) {
  const cls = [
    "d-flex justify-content-between align-items-center py-1",
    muted ? "text-white/60" : "",
    bold ? "fw-black" : "",
  ].join(" ");
  const valueCls = accent
    ? "text-emerald-300"
    : highlight
      ? "text-[#ffd166]"
      : "text-white";
  return (
    <div className={cls}>
      <span>{label}</span>
      <span className={`${valueCls} fw-bold`}>
        <i className="bi bi-currency-rupee" />
        {Number(value || 0)}
      </span>
    </div>
  );
}
